"use client";
import TextField from "../ui/textField";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { verifyOtpSchema } from "@/validations";
import { cn } from "@/lib";
import CustomButton from "../ui/customButton";
import { useRouter, useSearchParams } from "next/navigation";
import { TSendOtpPayload, TVerifyOtp, TVerifyOtpPayload } from "@/interfaces";
import ResendTimer from "./resendTimer";
import { useAppStore } from "@/store/general";
import {
  useGetUserTokenMutation,
  useSendOtpMutation,
  useVerifyOtpMutation,
} from "@/hooks";
import ErrorMessage from "../ui/errorMessage";
import { FlexBetween, FlexCenter } from "../ui/flexBox";
import { routes, staticQueryParams } from "@/routes";
import { useTranslations } from "next-intl";
import { useAuthStore, useModalStore } from "@/store";
import { useToastStore } from "@/store/toastStore";
import { useEffect } from "react";

const VerifySendOtpForm = ({
  backFormStep,
  nextFormStep,
  isPage = false,
  fromForgotPass,
}: {
  backFormStep?: () => void;
  nextFormStep?: () => void;
  isPage?: boolean;
  fromForgotPass?: boolean;
}) => {
  const { setToken } = useAuthStore();
  const searchParams = useSearchParams();
  const { setOpenToast } = useToastStore();
  const { closeModal } = useModalStore();
  const t = useTranslations("auth");
  const { phoneNumber: storedPhoneNumber } = useAppStore();
  const router = useRouter();
  const {
    isPending: isVerifying,
    mutate: verifyOtpFn,
    error: verifyOtpError,
  } = useVerifyOtpMutation();
  const {
    mutate: sendOtpFn,
    error: sendOtpError,
    isError: isSendOtpError,
  } = useSendOtpMutation();
  const {
    isPending: isGettingToken,
    mutate: getUserTokenFn,
    error: getUserTokenError,
  } = useGetUserTokenMutation();
  const openToast = () => {
    setOpenToast({
      variant: "success",
      message: "ورود شما با موفقیت انجام شد",
    });
  };
  const isError: Error | null =
    sendOtpError || verifyOtpError || getUserTokenError;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<TVerifyOtp>({
    resolver: zodResolver(verifyOtpSchema),
  });
  const { postLoginAction, setPostLoginAction } = useAppStore();
  const { openModal, setOpenModal } = useModalStore();
  const onSubmit = (data: TVerifyOtp) => {
    const payload: Readonly<TVerifyOtpPayload> = {
      mobile: storedPhoneNumber,
      otp: data.code,
    };
    if (fromForgotPass) {
      getUserTokenFn(payload, {
        onSuccess: (res) => {
          if (!res.data?.token) {
            throw new Error("عدم ارسال توکن");
          }
          setToken(res.data.token);
          if (isPage) {
            router.push(routes.auth.CHANGE_PASSWORD.primaryPath);
            return;
          }
          nextFormStep
            ? nextFormStep()
            : (closeModal(), window.location.reload());
        },
      });
      return;
    }
    verifyOtpFn(payload, {
      onSuccess: () => {
        if (isPage) {
          window.location.href =
            searchParams?.get(staticQueryParams.redirect) ||
            routes.main.HOME.primaryPath;
          openToast();
          return;
        }
        if (postLoginAction === "open-payment-modal") {
          closeModal();
          setTimeout(() => {
            setOpenModal("payment-method");
            setPostLoginAction(null);
          }, 100);
        }
        nextFormStep ? nextFormStep() : (closeModal(), openToast());
      },
    });
  };

  const handleResendOtpCode = () => {
    const payload: Readonly<TSendOtpPayload> = {
      mobile: storedPhoneNumber,
    };
    sendOtpFn(payload);
  };
  const handleBackStep = () => {
    if (isPage) {
      router.back();
      return;
    }
    if (backFormStep) {
      backFormStep();
    }
  };
  const code = watch("code");

  useEffect(() => {
    if (code && code.length === 5) handleSubmit(onSubmit)();
  }, [code]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={cn("mb-4 w-full")}>
      <FlexCenter className={cn("mb-4 gap-1 text-sm")}>
        <p>{t("currentPhoneNumber")}</p>
        <p>{storedPhoneNumber}</p>
      </FlexCenter>
      <fieldset>
        <TextField
          hasShadow
          {...register("code")}
          placeholder={t("placeholders.otpCode")}
          numeric
          inputMode="numeric"
          className="mb-2"
          aria-label="code"
          autoFocus
          maxLength={5}
          value={code}
          inputClassName="h-10"
          error={errors.code?.message}
        />
        <ResendTimer onResend={handleResendOtpCode} isError={isSendOtpError} />
      </fieldset>
      {isError && <ErrorMessage message={isError.message} />}
      <FlexBetween gap={4} className="mx-auto w-max">
        <CustomButton
          id="verify-otp-back-step"
          variant={"gray"}
          className="mt-8 w-max px-8!"
          onClick={handleBackStep}
          disabled={isVerifying || isGettingToken}
        >
          بازگشت
        </CustomButton>
        <CustomButton
          id="verify-otp-submit"
          type="submit"
          className="mt-8 w-max px-8!"
          isLoading={isVerifying || isGettingToken}
        >
          {t("buttons.verifyOtpSubmit")}
        </CustomButton>
      </FlexBetween>
    </form>
  );
};

export default VerifySendOtpForm;
